import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Map, Star, QrCode, Gift, Settings as SettingsIcon, LogOut } from 'lucide-react';
import MapView from '../components/MapView';
import FeaturedStores from '../components/FeaturedStores';
import MyQR from '../components/MyQR';
import Benefits from '../components/Benefits';
import Settings from '../components/Settings';
import Notifications from '../components/Notifications';
import SubscriptionRequired from '../components/SubscriptionRequired';
import { authService } from '../services/auth';
import { subscriptionService } from '../services/subscription';
import Footer from '../components/Footer';

const Home = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('map');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  useEffect(() => {
    const loadUserAndSubscription = async () => {
      try {
        setLoading(true);
        // Obtener información del usuario desde el backend
        const userData = await authService.getCurrentUserFromBackend();
        
        if (!userData) {
          // Si no hay usuario, redirigir al login
          navigate('/login');
          return;
        }

        setUser(userData);

        // Si es admin, no necesita suscripción
        if (userData.role === 'admin') {
          setHasActiveSubscription(true); // Admin siempre tiene acceso
          return;
        }

        // Solo verificar suscripción para usuarios cliente (no admin, no tienda)
        // Verificar si tiene suscripción activa
        // Primero verificar por subscription_status del usuario
        const subscriptionStatus = userData.subscription_status;
        
        // Estados activos: active, trialing
        // Estados inactivos: null, undefined, incomplete, incomplete_expired, past_due, canceled, unpaid, paused
        if (subscriptionStatus === 'active' || subscriptionStatus === 'trialing') {
          // Tiene suscripción activa según el campo del usuario
          setHasActiveSubscription(true);
        } else {
          // No tiene suscripción activa o no tiene suscripción
          // Verificar con el endpoint para estar seguros
          try {
            const statusResponse = await subscriptionService.getStatus();
            // Si el servicio retorna has_subscription: false, significa que no tiene suscripción
            if (statusResponse.has_subscription === false) {
              setHasActiveSubscription(false);
            } else if (statusResponse.success && statusResponse.data && statusResponse.data.can_access_premium) {
              setHasActiveSubscription(true);
            } else {
              setHasActiveSubscription(false);
            }
          } catch (subError) {
            // Si hay error (no relacionado con "no tiene suscripción"), loguear y asumir que no tiene
            console.warn('Error verificando estado de suscripción:', subError.message);
            setHasActiveSubscription(false);
          }
        }
      } catch (error) {
        console.error('Error cargando usuario:', error);
        // Si hay error, redirigir al login
        navigate('/login');
      } finally {
        setLoading(false);
        setCheckingSubscription(false);
      }
    };

    loadUserAndSubscription();
  }, [navigate]);

  if (loading || checkingSubscription) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-lg">Cargando...</div>
      </div>
    );
  }

  if (!user) {
    return null; // El useEffect redirigirá al login
  }

  // Si no tiene suscripción activa, mostrar el componente de suscripción requerida
  if (hasActiveSubscription === false) {
    return <SubscriptionRequired />;
  }

  // Si tiene suscripción activa o aún no se ha verificado, mostrar el dashboard normal
  // (por defecto mostrar el dashboard si tiene suscripción o si es admin)
  if (user.role === 'admin') {
    navigate('/admin');
    return null;
  }

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const tabs = [
    { id: 'map', label: 'Mapa', icon: Map },
    // { id: 'featured', label: 'Destacadas', icon: Star },
    { id: 'qr', label: 'Mi QR', icon: QrCode },
    { id: 'benefits', label: 'Beneficios', icon: Gift },
    { id: 'settings', label: 'Cuenta', icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Header */}
      <div className="px-6 py-8 border-b border-white/10">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white mb-1">
              Hola, {user.name || 'Usuario'}
            </h1>
            <p className="text-[#A2A2A2]">
              Empieza a usar tus beneficios
            </p>
            {user.member_since && (() => {
              const memberDate = new Date(user.member_since);
              const today = new Date();
              const diffTime = Math.abs(today - memberDate);
              const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
              
              return (
                <p className="text-[#A2A2A2] text-sm mt-1">
                  Miembro desde {memberDate.toLocaleDateString('es-ES', { 
                    year: 'numeric', 
                    month: 'long',
                    day: 'numeric'
                  })} ({diffDays} {diffDays === 1 ? 'día' : 'días'})
                </p>
              );
            })()}
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-all hover:scale-105 active:scale-95"
            title="Cerrar sesión"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm font-medium">Salir</span>
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto pb-24">
        {activeTab === 'map' && <MapView />}
        {activeTab === 'featured' && <FeaturedStores />}
        {activeTab === 'qr' && <MyQR />}
        {activeTab === 'benefits' && <Benefits />}
        {activeTab === 'settings' && <Settings />}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-black border-t border-white/10">
        <div className="max-w-md mx-auto flex justify-around items-center px-6 py-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex flex-col items-center gap-1 transition-all ${
                  isActive ? 'text-white' : 'text-[#A2A2A2] hover:text-white'
                }`}
              >
                <div className={`p-3 rounded-full transition-all ${
                  isActive ? 'bg-white' : 'bg-transparent hover:bg-white/10'
                }`}>
                  <Icon className={`w-5 h-5 ${
                    isActive ? 'text-black' : 'text-current'
                  }`} />
                </div>
                <span className="text-xs font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>
        <Footer className="pb-2" />
      </div>
    </div>
  );
};

export default Home;
